import { useEffect, useRef } from "react"

import cn from "classnames"

import { gsap } from "utils/gsap"

export default function ProgressBar({
  step,
  steps,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  step: number
  steps: number
}): JSX.Element {
  const barRef = useRef<HTMLDivElement>(null)
  const animation = useRef<GSAPAnimation | null>(null)

  useEffect(() => {
    if (!barRef.current) return
    const width = (step / steps) * 100
    if (animation.current) {
      animation.current.kill()
    }
    animation.current = gsap.to(barRef.current, {
      width: `calc(${width}% - ((125px / ${steps}) * ${step}))`,
      duration: 0.5,
      ease: "power2.inOut"
    })
  }, [step, steps])

  const wrapperClass = cn("relative overflow-auto", {
    [rest?.className as string]: !!rest?.className
  })
  rest = { ...rest, className: wrapperClass }

  // the width of the circle is 2.5em
  // the width of each item is 125px
  return (
    <div {...rest}>
      {/* Needed extra wrapping div inside the overflow (auto or scroll) so that the absolute bars will take the full width of the parent */}
      <div className="inline-block relative min-w-full">
        <div className="rounded absolute left-[62.5px] right-[62.5px] top-[1.25em] h-1 bg-gray-200 w-[calc(100%-125px)]" />
        <div
          className="rounded absolute left-[62.5px] right-[62.5px] top-[1.25em] h-1 bg-primary w-0"
          ref={barRef}
        />
        <div className="relative flex items-start justify-between gap-6">
          {children}
        </div>
      </div>
    </div>
  )
}

export const ProgressBarItem: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    disabled: boolean
  }
> = ({ children, disabled, ...rest }) => {
  const wrapperClass = cn("w-[125px] flex-shrink-0 text-center", {
    "cursor-not-allowed": disabled,
    "hover:scale-105 transition-transform cursor-pointer": !disabled,
    [rest?.className as string]: !!rest?.className
  })
  rest = { ...rest, className: wrapperClass }
  return (
    <div {...rest}>
      <div className="w-[2.5em] h-[2.5em] bg-white rounded-full border-2 mx-auto" />
      <p
        className={`text-sm uppercase font-normal tracking-widest ${
          disabled ? "opacity-50" : ""
        }`}
      >
        {children}
      </p>
    </div>
  )
}
